'use client'
import { Progress } from '@nextui-org/progress'

import { clsx } from 'clsx'

import { FC, useEffect, useState } from 'react'

import { LanguageLevel, Maybe } from '@/app/graphql/generated'
import { AnimationShowBlockComponent } from '@/app/shared/components/animation/show-block'
import { ListComponent } from '@/app/shared/components/list'
import { progress, styles } from '@/app/shared/components/program/elements/level-info/constants'
import { IconArrowSecond } from '@/app/shared/icons'
import { clearList } from '@/app/shared/services/clearList'
import useLayout from '@/app/shared/store/layout.store'
import { ButtonComponent } from '@/app/shared/ui/button'
import { TitleComponent } from '@/app/shared/ui/title'
import { t } from '@/core/lib/locale/i18n'

import { AnimationHeightComponent } from '../../../../ui/animation-height'

// interface
interface ILevelInfo {
  data?: Maybe<LanguageLevel>
  index: number
}

type TProgress = 'vocabulary_percent' | 'grammar_percent' | 'mastery_percent'

// component
const LevelInfoComponent: FC<Readonly<ILevelInfo>> = ({ data, index }) => {
  const dataProgress = progress.map((el) => ({
    name: el.name,
    percent: data?.[el.slug as TProgress],
  }))

  const typeSite = useLayout((state) => state.typeSite)

  const [topics, setTopics] = useState<string[]>([])
  const [grammar, setGrammar] = useState<string[]>([])

  useEffect(() => {
    setTopics(clearList(data?.topics ?? ''))

    setGrammar(clearList(data?.grammar_skills ?? ''))
  }, [data?.topics, data?.grammar_skills])

  // return
  return (
    <AnimationShowBlockComponent
      className={'overflow-hidden rounded-large border-2 border-warning'}
      initial={{
        opacity: 0,
        y: 30,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
      }}
    >
      <div
        className={
          'grid gap-5 bg-warning p-[30px] text-content1-200 extraMobile:p-10 desktop:gap-10'
        }
      >
        <div
          className={
            'grid grid-flow-col items-center justify-between gap-4 laptop:flex laptop:gap-0'
          }
        >
          <TitleComponent size={'md'}>{data?.name2}</TitleComponent>
          <div className={'flex flex-col items-end'}>
            <span
              className={clsx(
                {
                  'text-base font-extrabold laptop:text-2xl':
                    typeSite === 'online' || typeSite === 'offline' || typeSite === 'kids',
                },
                {
                  'text-[34px] font-extrabold desktop:text-[44px]':
                    typeSite === 'online-espanol' || typeSite === 'online-deutsch',
                },
              )}
            >
              {typeSite === 'offline' || typeSite === 'online'
                ? t.level_code({ number: index + 1 })
                : data?.language_level_code}
            </span>
            {(typeSite === 'online' || typeSite === 'offline') && (
              <span className={'text-base font-extrabold laptop:text-2xl'}>
                {data?.language_level_code?.split(' ')[1]}
              </span>
            )}
          </div>
        </div>
        <div
          className={
            'grid gap-x-2.5 mobile:grid-cols-2 laptop:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] laptop:gap-[30px] desktop:grid-cols-3 large:gap-[78px]'
          }
        >
          {dataProgress.map((el: any, index: number) => (
            <div
              key={index}
              className={'flex flex-col text-base font-semibold desktop:text-[22px]'}
            >
              <span className={'mb-2.5 text-black desktop:mb-5'}>{el.name}</span>
              <Progress
                value={el.percent}
                className={'mb-2.5 mt-auto w-full rounded-[24px] border-1 border-content1-200'}
                classNames={styles}
                aria-label={el.name}
              />
              <span>{el.percent}%</span>
            </div>
          ))}
        </div>
      </div>
      <AnimationHeightComponent
        className={'p-5 extraMobile:p-10'}
        contentClass={
          'grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] desktop:grid-cols-3 items-start gap-10'
        }
        startHeight={'18rem'}
        slotBtn={(handleToggle, open) => (
          <ButtonComponent
            variant={'light'}
            onClick={handleToggle}
            className={
              'absolute bottom-0 left-0 flex h-20 w-full items-center justify-center bg-gradient-to-b from-transparent via-content1-200 to-content1-200 text-[16px] font-extrabold uppercase data-[hover=true]:bg-inherit data-[hover=true]:text-warning'
            }
          >
            {open ? t.hide_more() : t.show_more()}
            <IconArrowSecond
              className={clsx(
                'ml-2 h-[30px] w-[30px] rotate-45 text-warning transition-transform',
                {
                  '!-rotate-45': open,
                },
              )}
            />
          </ButtonComponent>
        )}
      >
        <div className={'grid gap-5'}>
          <span className={'text-2xl font-extrabold'}>Теми:</span>
          <ListComponent data={topics} colorIcon={'warning'} noAnimation editor />
        </div>
        <div className={'grid gap-5'}>
          <span className={'text-2xl font-extrabold'}>Граматика:</span>
          <ListComponent data={grammar} colorIcon={'warning'} noAnimation editor />
        </div>
        <div className={'grid gap-5'}>
          <span className={'text-2xl font-extrabold'}>Результат:</span>
          <p>{data?.result}</p>
        </div>
      </AnimationHeightComponent>
    </AnimationShowBlockComponent>
  )
}

export default LevelInfoComponent
